import React, { useRef, useEffect, useCallback, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import SignatureCanvas from "react-signature-canvas";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, ButtonGroup } from "reactstrap";
import { fetcher } from "api/client";
import { clientContractAction } from "api/actions/contracts";
import bootbox from "components/Modals/Bootbox";
import { useSign, SIGN_MODAL, PHONE_MODAL, CONTROL_WAITER, CONTROL_MODAL, CHANGE_PREVIEW, SIGNUP_MODAL } from "context/signview";
import { usePosition } from "hooks/usePosition";
import useWindowDimensions from "hooks/useWindowDimensions";

const width = 450;
const height = 200;
const widthRatio = 2;

const Sign = forwardRef((props, ref) => {
    ref.current?.fromData(JSON.parse(props.coords || "[]"));
    return (
        <SignatureCanvas
            ref={ref}
            backgroundColor={"white"}
            canvasProps={{
                width,
                height,
            }}
        />
    );
});

export default function Signature() {
    const [{ modal, contractId, signatureConfirmations, requestLocation, profile, jwt, contract }, dispatch] = useSign();
    const { t } = useTranslation();
    const { latitude, longitude, getLocation } = usePosition();
    const getWindowDimensions = useWindowDimensions();
    const queryClient = useQueryClient();
    const sigCanvas = useRef({});
    const sigPad = useRef({});

    const { mutate: signatureMutation } = useMutation((payload) => fetcher(clientContractAction(contractId, "sign", payload), jwt), {
        onSuccess: ({ status, importedPdf }) => {
            if (!profile.config?.hideSignUpModal && !contract.signViewOptions?.isTratoUser) {
                dispatch({ type: CONTROL_MODAL, open: SIGNUP_MODAL })
            }
            if (status === "SENT_TO_SIGN") {
                bootbox.alert({
                    text: `${t("sign.WaitToSign.body_one")} ${t("sign.WaitToSign.body_two")}`,
                    title: t("sign.WaitToSign.title_signed"),
                });
            }

            if (importedPdf) dispatch({ type: CHANGE_PREVIEW, previewUrl: importedPdf });
            handleClose();
            queryClient.invalidateQueries("client-contract");
        },
        onSettled: () => {
            dispatch({ type: CONTROL_WAITER, open: false });
        },
    });

    const handleSign = () => {
        if (sigPad.current.isEmpty()) return bootbox.alert(t("errors.SignatureIsEmpty"));
        const coords = sigPad.current.toData();
        if (coords.flat().length < 10) return bootbox.alert(t("sign.sign.autograph_not_valid"));
        dispatch({ type: CONTROL_WAITER, open: true });
        signatureMutation({
            signatureCoords: { coords: JSON.stringify(coords), width: sigCanvas.current.width, height: sigCanvas.current.height },
            signatureConfirmations: Object.keys(signatureConfirmations).map((s) => signatureConfirmations[s]),
            geolocation: requestLocation ? { latitude, longitude } : {},
        });
    };

    const handleClearSignature = () => {
        sigPad.current.clear();
    };

    const resizeSignature = (signature) => {
        const WIDTH = sigCanvas.current.width;
        const HEIGHT = sigCanvas.current.height;
        console.log(WIDTH)
        console.log(HEIGHT)
        const result = [];
        const data = JSON.parse(signature.coords);
        for (let i = 0; i < data.length; i++) {
            const newData = data[i].map((d) => ({
                ...d,
                x: (d.x / signature.width) * WIDTH,
                y: (d.y / signature.height) * HEIGHT,
            }));
            result.push(newData);
        }
        return result;
    };

    const handleClose = () => {
        dispatch({ type: CONTROL_MODAL, close: SIGN_MODAL });
    };

    const handleUseSignature = () => {
        if (sigPad?.current?.fromData) {
            sigPad.current.fromData(resizeSignature(profile?.originalSignatureCoords || "[]"));
        }
    };

    const handleAskGeoLocation = async () => {
        await bootbox.alert({
            text: t("sign.errors.ask_geolocation"),
            title: t("editor.config.geolocation_required"),
        });
        getLocation();
    };

    const changeZoom = () => {
        let viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
            viewport.content = "initial-scale=1";
            viewport.content = "width=device-width";
        }
    };

    const measuredRef = useCallback(
        (node) => {
            const resizeCanvas = (signaturePad, canvas) => {
                canvas.width = canvas.parentElement.clientWidth;
                canvas.height = canvas.parentElement.clientWidth / widthRatio;
                signaturePad.clear();
            };

            if (node !== null) {
                sigCanvas.current = node.getCanvas();
                sigPad.current = node.getSignaturePad();
                resizeCanvas(node.getSignaturePad(), node.getCanvas());
            }
        },
        [getWindowDimensions]
    );

    useEffect(() => {
        changeZoom();
        if (modal.includes(SIGN_MODAL)) {
            changeZoom();
        }
    }, [modal]);

    return (
        <Modal isOpen={modal.includes(SIGN_MODAL)} centered onOpened={changeZoom}>
            <ModalHeader toggle={handleClose}>
                <span>{t("sign.sign_modal.modal_title")}</span> <small className="text-muted">{t("profile.signature.modal_subtitle")}</small>
            </ModalHeader>
            <ModalBody className="bg-light px-0 pb-0 text-center">
                <div className="canvasWrapper">
                    <Sign ref={measuredRef} />
                    <Button color="default" className="mb-1" onClick={handleClearSignature}>
                        {t("sign.sign_modal.modal_clear")}
                    </Button>
                    {profile.signatureCoords && !profile.isPluginClient && (
                        <Button color="default" className="ml-1 mr-n3 mb-1" onClick={handleUseSignature}>
                            {t("sign.sign.autograph_mine")}
                        </Button>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup className="d-flex flex-grow-1">
                    {requestLocation && !latitude ? (
                        <Button size="xl" color="primary" className="flex-even" onClick={handleAskGeoLocation}>
                            {t("sign.errors.geolocalizationRequired")}
                            {/* {t('sign.sign_modal.sign')} */}
                        </Button>
                    ) : (
                        <Button size="xl" color="primary" className="flex-even" onClick={handleSign}>
                            {t("sign.sign_modal.sign")}
                        </Button>
                    )}
                </ButtonGroup>
            </ModalFooter>
        </Modal>
    );
}
